import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { importCodelist } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type { MappingKind } from '../../api/types';
import { useErrorToast, useSuccessToast } from '../../hooks/useErrorToast';
import { parseCodelistJson, type ParseResult } from './codelistJson';
import { kindLabelKeys } from './codelistLabels';

export interface ImportCodelistDialogProps {
  /** The kind the draft is imported under — the tab the administrator has open, whatever the file says. */
  kind: MappingKind;
  onClose: () => void;
}

export function ImportCodelistDialog({ kind, onClose }: ImportCodelistDialogProps) {
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const { t } = useTranslation();

  const [versionLabel, setVersionLabel] = useState('');
  const [sourceNote, setSourceNote] = useState('');
  const [json, setJson] = useState('');
  const [parsed, setParsed] = useState<ParseResult | null>(null);

  const handleJsonChange = (text: string) => {
    setJson(text);
    if (text.trim() === '') {
      setParsed(null);
      return;
    }

    const result = parseCodelistJson(text);
    setParsed(result);
    // A whole import file carries its own label and note; take them over only into empty fields, so
    // something the administrator already typed is never overwritten by a paste.
    if (result.ok) {
      if (versionLabel.trim() === '' && result.value.versionLabel) setVersionLabel(result.value.versionLabel);
      if (sourceNote.trim() === '' && result.value.sourceNote) setSourceNote(result.value.sourceNote);
    }
  };

  const mutation = useMutation({
    mutationFn: () => {
      if (!parsed?.ok) throw new Error('Nothing to import.');
      return importCodelist({
        kind,
        versionLabel: versionLabel.trim(),
        sourceNote: sourceNote.trim() === '' ? null : sourceNote.trim(),
        entries: parsed.value.entries,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.codelists.all });
      toastSuccess(t('codelists.versions.imported'));
      onClose();
    },
    onError: toastError,
  });

  const fileKind = parsed?.ok ? parsed.value.kind : null;
  const kindMismatch = fileKind !== null && fileKind !== kind;
  const jsonError =
    parsed !== null && !parsed.ok ? t(parsed.reasonKey, { detail: parsed.detail ?? '' }) : null;
  const canSubmit = parsed?.ok === true && versionLabel.trim() !== '' && !mutation.isPending;

  return (
    <Dialog open onClose={mutation.isPending ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('codelists.versions.importDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('codelists.versions.importDialog.kind', { kind: t(kindLabelKeys[kind]) })}
          </Typography>

          <TextField
            label={t('codelists.versions.importDialog.versionLabel')}
            value={versionLabel}
            onChange={(event) => setVersionLabel(event.target.value)}
            required
            slotProps={{ htmlInput: { maxLength: 50 } }}
          />

          <TextField
            label={t('codelists.versions.importDialog.sourceNote')}
            value={sourceNote}
            onChange={(event) => setSourceNote(event.target.value)}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />

          {kindMismatch && (
            <Alert severity="warning">
              {t('codelists.versions.importDialog.kindMismatch', {
                fileKind: t(kindLabelKeys[fileKind]),
                kind: t(kindLabelKeys[kind]),
              })}
            </Alert>
          )}

          <TextField
            label={t('codelists.versions.importDialog.json')}
            value={json}
            onChange={(event) => handleJsonChange(event.target.value)}
            multiline
            minRows={12}
            maxRows={24}
            required
            error={jsonError !== null}
            helperText={jsonError ?? t('codelists.versions.importDialog.jsonHelp')}
            slotProps={{ htmlInput: { sx: { fontFamily: 'monospace', fontSize: 13 }, spellCheck: false } }}
          />

          {parsed?.ok && (
            <Typography variant="body2" color="text.secondary">
              {t('codelists.versions.importDialog.parsedCount', { count: parsed.value.entries.length })}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          {t('actions.cancel')}
        </Button>
        <Button variant="contained" onClick={() => mutation.mutate()} disabled={!canSubmit}>
          {t('codelists.versions.importDialog.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
